import { useState, useEffect } from 'react';
import { callUIAPI } from '../../../utils/apiUtils';
import * as apiConstants from '../../../utils/apiUtilsConstants';

interface AssistantRoute {
  path: string;
  name: string;
  group: string;
}

export interface PopularRoute {
  label: string;
  message: string;
}

interface AssistantConfig {
  assistantEnabled: boolean;
  trainingEnabled: boolean;
  supportRequestEnabled: boolean;
  chatCopyEnabled: boolean;
  routes: AssistantRoute[];
  popularRoutes: PopularRoute[];
}

const toPopularRoutes = (value: unknown): PopularRoute[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    const label = typeof entry?.label === 'string' ? entry.label.trim() : '';
    if (!label) return [];
    const message = typeof entry?.message === 'string' && entry.message.trim() ? entry.message.trim() : label;
    return [{ label, message }];
  });
};

let cachedConfig: AssistantConfig | null = null;
let fetchPromise: Promise<AssistantConfig> | null = null;

/**
 * Hook that fetches and caches the assistant config for the session.
 * Only makes one API call — subsequent calls return the cached value.
 */
export function useAssistantConfig() {
  const [config, setConfig] = useState<AssistantConfig | null>(cachedConfig);
  const [loading, setLoading] = useState(!cachedConfig);

  useEffect(() => {
    if (cachedConfig) {
      setConfig(cachedConfig);
      setLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = callUIAPI('assistant/config', undefined, apiConstants.METHOD_GET, true)
        .then((response) => {
          const cfg: AssistantConfig = {
            assistantEnabled: response.data?.assistantEnabled === true,
            trainingEnabled: response.data?.trainingEnabled === true,
            supportRequestEnabled: response.data?.supportRequestEnabled === true,
            chatCopyEnabled: response.data?.chatCopyEnabled === true,
            routes: Array.isArray(response.data?.routes) ? response.data.routes : [],
            popularRoutes: toPopularRoutes(response.data?.popularRoutes),
          };
          cachedConfig = cfg;
          return cfg;
        })
        .catch(() => {
          const fallback: AssistantConfig = {
            assistantEnabled: false,
            trainingEnabled: false,
            supportRequestEnabled: false,
            chatCopyEnabled: false,
            routes: [],
            popularRoutes: [],
          };
          cachedConfig = fallback;
          return fallback;
        });
    }

    fetchPromise.then((cfg) => {
      setConfig(cfg);
      setLoading(false);
    });
  }, []);

  return { config, loading };
}